import _ from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { checkCardFromRule } from '../../../component/card/card-from';
import GlobalNotification from '../../../component/common/snackbar';
import BASE_CHECK_TYPE from '../../../constants/regular.constant';
import i18n from '../../../i18n';
import { Group } from '../../../models/group.entity';
import { Staff } from '../../../models/staff.entity';
import apiManage from '../../../request';
import { NewResponsible } from '../../../request/api/staff';
import { GroupAccountCardArr } from './group-account';
import LSM from '../../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../../modules/cache/local-storage.constant';

/**
 * 集团账号信息详情数据管理
 */
export class GroupAccountState implements GroupInfoData {

    constructor(props: GroupProps) {
        makeAutoObservable(this);
        this.belongedGroupId = props.belongedGroupId;
        this.belongedGroup = props.belongedGroup;
        this.componentType = props.componentType;
    }

    // *********************
    // Attributes
    // *********************

    // 组件类型
    componentType: 'EDIT' | 'PREVIEW' | 'ADD' | undefined = 'ADD';
    // 集团_id
    belongedGroupId: string | undefined = '';
    // 集团string_id
    belongedGroup: string | undefined = '';
    // 经销商_id
    belongedResellerId: string | undefined = '';
    // 是否启用骨架屏加载  默认进来页面开启
    isEnableSkeleton: boolean = true;
    // 编辑输入框操作map
    @observable operateMap: Map<string, { origin: number, current: number }> = new Map();
    // 集团编辑信息
    groupEditDetail: Group = {
        // 集团名称
        nickname: '',
        // 街道地址
        local: '',
        // 邮箱
        email: '',
        // 电话
        tel: '',
        // 电子发票收件人编码
        reg_des_code: '',
        // 法务联系邮箱
        reg_pec: '',
        // 增值税号
        reg_vat_number: '',
        // 税务注册号
        reg_fiscal_code: '',
        // 邮政编码
        postcode: '',
        // 城市
        city: '',
        // 省
        state: '',
        // 国家
        country: '',
        // 国家区号
        phone_prefix: '39',
        // 门铃
        home_number: '',
        // 集团string_id
        string_id: '',
        // 集团头像logo
        avatar: '',
    };
    // 输入框是否可以输入编辑
    isEditInput: boolean = false;
    // 输入框是否为空校验
    isEmptyVerify: boolean = false;
    // 保存按钮禁用
    disabledBtn: number = 0;
    // 集团账号源数据
    originGroupData: Group & { file?: File, originAvatar?: string } = {};
    // 负责人信息弹窗配置
    dialogConfig: {
        // 打开弹窗
        visible: boolean;
        // 弹窗标题
        title?: string;
        // 关闭弹窗
        onClose?: () => void;
    } = {
            visible: false,
            title: '',
            onClose: () => { },
        }
    // 删除弹框配置
    deleteDialogConfig: {
        visible: boolean;
        title: string;
        content: string;
        forbidConfirm?: number;
        handleClick?: () => void;
    } = {
            visible: false,
            title: '',
            content: '',
            forbidConfirm: 0
        };


    // *********************
    // Business Function
    // *********************

    // 初始化
    async init() {
        this.initGroupData();
        // 获取集团_id
        // this.belongedGroupId = this.belongedGroupId;
        // // 获取集团stringId
        // this.belongedGroup = this.belongedGroup;
        // 编辑和请求才请求数据
        if (this.componentType !== 'ADD' && this.belongedGroupId) {
            // 获取账号数据
            await this.getGroupAccountData(this.belongedGroupId);
            // 关闭骨架屏
            runInAction(() => {
                this.isEnableSkeleton = false;
            })
        }
    }

    // 初始化弹窗 新建用
    initGroupData() {
        this.groupEditDetail = {
            // 集团名称
            nickname: '',
            // 街道地址
            local: '',
            // 邮箱
            email: '',
            // 电话
            tel: '',
            // 电子发票收件人编码
            reg_des_code: '',
            // 法务联系邮箱
            reg_pec: '',
            // 增值税号
            reg_vat_number: '',
            // 税务注册号
            reg_fiscal_code: '',
            // 邮政编码
            postcode: '',
            // 城市
            city: '',
            // 省
            state: '',
            // 国家
            country: '',
            // 国家区号
            phone_prefix: this.groupEditDetail.phone_prefix,
            // 门铃
            home_number: '',
            // 集团string_id
            string_id: '',
            // 集团头像logo
            avatar: '',
        };
        this.originGroupData = Object.assign(this.groupEditDetail, { originAvatar: '' });
        this.operateMap.clear();
        this.setIsEmptyFlag(false);
    }

    /**
     * 获取集团账号数据
     */
    async getGroupAccountData(groupId?: string): Promise<void> {
        /**
         * 1.设置标记状态为禁用
         * 2.成功之后才可以编辑
         * 3.关闭骨架屏
         */
        try {
            const id = groupId || this.belongedGroupId;
            const result = await apiManage.get_group_id_id(id);
            if (!result?.error) {
                // 获取到手机前缀
                // const prefix = +(result?.phone_prefix ?? '39');
                // 根据手机前缀获取对应国家区号
                // const phone_prefix = getRelatedCode(prefix);
                // 集团logo
                let groupLogo = '';
                if (result?.logo?._id) {
                    groupLogo = await this.getGroupLogoUrl(result?.logo?._id);
                }
                // 数据组装保存
                const groupData: Group = {
                    // 集团名称
                    nickname: result?.nickname || '',
                    // 街道地址
                    local: result.local || result?.reg_local || '',
                    // 邮箱
                    email: result?.email || '',
                    // 电话
                    tel: result?.tel || '',
                    // 电子发票收件人编码
                    reg_des_code: result?.reg_des_code || '',
                    // 法务联系邮箱
                    reg_pec: result?.reg_pec || '',
                    // 增值税号
                    reg_vat_number: result?.reg_vat_number || '',
                    // 税务注册号
                    reg_fiscal_code: result?.reg_fiscal_code || '',
                    // 邮政编码
                    postcode: result?.postcode || result?.reg_postcode || '',
                    // 城市
                    city: result?.city || result?.reg_city || '',
                    // 省
                    state: result?.state || result?.reg_state || '',
                    // 国家
                    country: result?.country || result?.reg_country || '',
                    // 国家区号
                    phone_prefix: result.phone_prefix || '',
                    // 门铃
                    home_number: result?.home_number || '',
                    // 集团string_id
                    string_id: result?.string_id || '',
                    // 集团头像logo
                    avatar: groupLogo,
                }
                // 源数据
                const originGroupData = Object.assign(result, { originAvatar: groupLogo })
                // 设置界面数据
                this.setGroupAccountData(groupData, originGroupData);
                // 设置经销商id
                this.setBelongedResellerId(result.belonged_reseller as any|| '');
            } else {
                // 获取失败
                GlobalNotification.error(`${i18n.t('group_info_page_message_failed_obtain_group_information')}`)
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('group_info_page_message_failed_obtain_group_information')}`)
        }
    }

    // 设置集团账号界面数据
    setGroupAccountData(groupData: Group, originData: Group) {
        this.groupEditDetail = groupData;
        // 设置源数据 用于比对数据是否修改
        this.originGroupData = originData;
    }

    // 设置经销商id
    setBelongedResellerId(value: string) {
        this.belongedResellerId = value;
    }

    // 获取到集团logo
    async getGroupLogoUrl(id: string): Promise<string> {
        let groupImg = '';
        const result = await apiManage.get_group_id_id_logo(id);
        if (!result?.error) {
            groupImg = result;
        }
        return groupImg;
    }

    // 检查信息是否合法
    checkInfo(): boolean {
        const { nickname, email, tel } = this.groupEditDetail;
        if (!BASE_CHECK_TYPE['inputLimit120'].regular.test(nickname!)) {
            GlobalNotification.error(`${i18n.t('create_group_component_message_invalid_group_name')}${i18n.t('general_text_incompatible_rules')}`);
            return false
        }
        if (email !== '' && !BASE_CHECK_TYPE['email'].regular.test(email || '')) {
            GlobalNotification.error(`${i18n.t('create_group_component_message_invalid_email')}${i18n.t('general_text_incompatible_rules')}`);
            return false
        }
        // 电话选填，只有填写后才检查格式。
        if (tel && !BASE_CHECK_TYPE['phone'].regular.test(tel)) {
            GlobalNotification.error(`${i18n.t('create_group_component_message_invalid_phone')}${i18n.t('general_text_incompatible_rules')}`);
            return false
        }
        return true;
    }

    // 检查输入框必填项是否为空
    checkIsEmpty() {
        const { nickname } = this.groupEditDetail;
        if (nickname === '') {
            GlobalNotification.error(`${i18n.t('create_group_component_message_invalid_group_name')}${i18n.t('general_text_incompatible_rules')}`);
            return true
        }
        return false
    }

    // 设置为空标识
    setIsEmptyFlag(value: boolean) {
        this.isEmptyVerify = value;
    }

    /**
     * 集团账号保存函数
     * @param saveType 保存类型: 新增保存 | 编辑保存
     * @returns
     */
    async onSave(saveType: 'ADD' | 'EDIT') {
        // 成功标识
        let successFlag: boolean = true;
        let successResult: Group = {};
        try {
            // 检查必填项  校验必填规则
            const errorList = checkCardFromRule({ config: GroupAccountCardArr, data: this.groupEditDetail as {} });
            this.setIsEmptyFlag(true);
            if (errorList?.length) {
                return { successFlag: false, group: successResult };
            }
            // 源数据
            const originGroup = this.originGroupData;
            const { nickname, email, tel, reg_des_code, reg_pec, reg_vat_number, reg_fiscal_code, home_number, local,
                city, state, country, postcode, phone_prefix, avatar } = this.groupEditDetail;
            // 新增操作
            if (saveType === 'ADD') {
                const params = {
                    nickname,
                    reg_name: nickname,
                    local,
                    city,
                    state,
                    country,
                    postcode,
                    reg_vat_number,
                    reg_des_code,
                    reg_pec,
                    reg_fiscal_code,
                    home_number,
                    email,
                    tel,
                    phone_prefix
                };
                // 获取当前账户的信息
                const userInfo = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT);
                if (userInfo.account_type === 'RESELLER') {
                    _.set(params, 'belonged_reseller', userInfo._id);
                }
                // 请求接口
                const result = await apiManage.post_group(params);
                // 请求成功  TODO 这里的成功返回待进一步确认
                if (!result?.error) {
                    const { file } = this.originGroupData;
                    if (file && avatar !== '') {
                        const id = result?.groupResult?.insertedId || '';
                        const logoResult = await apiManage.patch_group_id_logo(id, file);
                        if (logoResult?.error) {
                            this.showNotificationMessage(i18n.t('create_group_component_message_failed_upload_group_picture'), 'error');
                        }
                    }
                    this.showNotificationMessage(i18n.t('create_group_component_message_new_group_success'), 'success');
                    // 清空表单随后刷新数据
                    // this.initCreateGroupDialog();
                    // // 成功后刷新数据
                    // this.getGroupListData();
                    successResult = _.get(result, 'groupResult.ops[0]') as Group;
                } else {
                    successFlag = false;
                    if (result?.result?.code === 1008) {
                        // 检查返回的错误信息是否存在(根据提交给后端的信息去给后端判断是否存在)
                        this.checkReturnInfoIsExists(result);
                    }
                    else this.showNotificationMessage(i18n.t('create_group_component_message_new_group_failed'), 'error');
                }
            }
            // 编辑操作
            else {
                // 保存所用的参数
                // 表单有更改才发送有变化的数据
                const params: Group = {
                    _id: originGroup._id,
                    nickname: nickname !== originGroup?.nickname ? nickname : undefined,
                    email: email !== originGroup?.email ? email : undefined,
                    tel: tel !== originGroup?.tel ? tel : undefined,
                    reg_des_code: reg_des_code !== originGroup?.reg_des_code ? reg_des_code : undefined,
                    reg_vat_number: reg_vat_number !== originGroup?.reg_vat_number ? reg_vat_number : undefined,
                    reg_pec: reg_pec !== originGroup?.reg_pec ? reg_pec : undefined,
                    reg_fiscal_code: reg_fiscal_code !== originGroup?.reg_fiscal_code ? reg_fiscal_code : undefined,
                    city: city !== originGroup?.city ? city : undefined,
                    country: country !== originGroup?.country ? country : undefined,
                    state: state !== originGroup?.state ? state : undefined,
                    postcode: postcode !== originGroup?.postcode ? postcode : undefined,
                    local: local !== originGroup?.local ? local : undefined,
                    home_number: home_number !== originGroup?.home_number ? home_number : undefined,
                    phone_prefix: phone_prefix !== originGroup.phone_prefix ? phone_prefix : undefined,
                }
                // 开启编辑禁用
                this.enableForbid();
                // 判断图片是否更改过
                if (avatar !== originGroup.originAvatar) {
                    const logoResult = await apiManage.patch_group_id_logo(originGroup._id!, originGroup.file ?? {} as File);
                    // 请求成功
                    if (logoResult?.error) {
                        this.showNotificationMessage(
                            `${i18n.t('create_group_component_message_failed_upload_group_picture')}`,
                            'error'
                        );
                    } else {
                        this.showNotificationMessage(i18n.t('group_info_page_message_group_picture_uploaded_successfully'), 'success');
                    }
                }
                // 大于1标识有改动 集团修改
                if (this.checkFormIsEdited()) {
                    const result = await apiManage.patch_group_id_id_info(params);
                    // 请求成功
                    if (!result?.error) {
                        this.showNotificationMessage(i18n.t('group_info_page_message_successfully_modify_group_information'), 'success')
                        // 刷新界面数据
                        this.getGroupAccountData(originGroup._id ?? '');
                    } else {
                        if (result?.result?.code === 1008) {
                            // 检查返回的错误信息是否存在(根据提交给后端的信息去给后端判断是否存在)
                            this.checkReturnInfoIsExists(result);
                        }
                        else this.showNotificationMessage(i18n.t('group_info_page_message_failed_modify_group_information'), 'error');
                    }
                }
                // 关闭编辑禁用
                this.closeForbid();
            }
            this.operateMap.clear();
            return {successFlag, group: successResult}
        } catch (error) {
            successFlag = false;
            this.operateMap.clear();
            saveType === 'ADD' ?
                this.showNotificationMessage(i18n.t('create_group_component_message_new_group_failed'), 'error')
                :
                this.showNotificationMessage(i18n.t('group_info_page_message_failed_modify_group_information'), 'error');
            return {successFlag, group: successResult}
        }
    }

    // 显示通知信息
    showNotificationMessage(message: string, type: 'success' | 'error') {
        type === 'success' ? GlobalNotification.success(message) : GlobalNotification.error(message);
    }

    // 校验表单是否修改
    checkFormIsEdited() {
        if (this.operateMap.size !== 0) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * 检查后端错误信息返回对比当前填写信息是否存在重复
     * @param result 错误信息的返回
     */
    checkReturnInfoIsExists(errorResult: any) {
        /**
         * 1.根据后端返回的报错信息键值判断是否存在相同的集团或号码
         * 2.提示存在重复
         */
        // 查找后端返回哪一个键值重复了
        const key = (errorResult?.result?.details || '').match(/(\S*):/)[1];
        // 拿出对应国际化的信息的字段
        const existsField = this.getExistsInfo(key)[0];
        // 判断数据库是否存在相同的值  提示重复了
        _.includes(Object.keys(existsField), key) ?
            this.showNotificationMessage(`${i18n.t(existsField[key])}${i18n.t('general_text_already_existed')}`, 'error')
            : this.showNotificationMessage(`${errorResult?.result?.details || ''}`, 'error');
    }

    /**
     * 获取存在重复的信息
     * @param key 后端返回的对应的错误的key值
     * @returns
     */
    getExistsInfo(key: string) {
        const labelArr: any[] = [];
        GroupAccountCardArr.forEach((item) => {
            item?.children && item?.children?.forEach((childItem) => {
                if (childItem.key === key && childItem.inputType === 'INPUT') {
                    labelArr.push({ [childItem.key]: childItem.label });
                }
            })
        });
        return labelArr;
    }

    // 开启输入框禁用和按钮禁用加载
    enableForbid() {
        this.disabledBtn = 1;
        this.isEditInput = true;
    }

    // 关闭输入框禁用和按钮禁用加载
    closeForbid() {
        this.disabledBtn = 0;
        this.isEditInput = false;
    }

    // 改变文本框输入
    handleChangeText(value: string, key: string): void {
        const curKeyVal = value;
        const originKeyVal = this.originGroupData[key as keyof Group];
        // 判断数据是否编辑过
        if (originKeyVal !== curKeyVal) {
            // _.set(this.groupEditDetail, [key], curKeyVal);
            this.operateMap.set(key, { origin: Number(originKeyVal), current: Number(curKeyVal) })
        }
        else this.operateMap.delete(key);
        _.set(this.groupEditDetail, [key], curKeyVal);
    }

    // 上传图片 更改头像文件
    async onChangeAvatar(files: File) {
        /**
         * 1.校验是否为图片以及是否存在
         * 2.对File类型转为url
         */
        const file = files;
        if (!file || !BASE_CHECK_TYPE['imageCheck'].regular.test(file?.type)) {
            GlobalNotification.error(`${i18n.t('general_message_image_type_incorrect_upload_failed')}`);
            return;
        } else {
            // 转为url链接
            const url = window.URL.createObjectURL(file);
            // 设置头像地址
            _.set(this.groupEditDetail, 'avatar', url);
            // 设置头像文件
            this.originGroupData['file'] = file;
        }
    }

    // *********************
    // 弹窗相关函数
    // *********************

    // 设置负责人信息弹窗数据
    setDialogConfig(data: typeof this.dialogConfig) {
        this.dialogConfig = {
            visible: data.visible,
            title: data.title,
            onClose: () => { return this.setDialogConfig({ visible: false }) }
        };
    }

    /**
     * 更新删除弹窗
     * @param data
     */
    updateDeleteDialogConfig(data: typeof this.deleteDialogConfig) {
        this.deleteDialogConfig = data;
    }

    /**
     * 删除并解绑负责人信息
     * @param selected 删除的数据
     * @param isBatch 是否为批量删除 不是则调用单独删除
     */
    async handleDeleteBindAccount(selected: Staff | Staff[], isBatch: boolean) {
        /**
         * 1.获取到所选中的id
         * 2.移除绑定
         * 3.刷新数据
         */
        try {
            // 判断是否为批量删除
            const params: NewResponsible = isBatch ? {
                staffIds: (selected as Staff[])?.map(item => item?._id) || [],
                type: 'GROUP',
                option: 'REMOVE',
                responsible_group: (selected as Staff[])?.[0].belonged_group as string ?? ''
            } : {
                _id: (selected as Staff)?._id || '',
                type: 'GROUP',
                responsible_group: (selected as Staff)?.belonged_group as string ?? '',
                option: 'REMOVE'
            }
            // 请求数据 isBatch 批量删除标识
            const result = isBatch ? await apiManage.patch_staff_ids_responsibles(params) : await apiManage.patch_staff_id_id_responsible(params);
            if (!result?.error) {
                // 刷新集团账号界面数据
                await this.getGroupAccountData(this.belongedGroupId);
                GlobalNotification.success(`${i18n.t('responsible_person_info_component_message_successfully_untie_group_leader')}`);
            } else {
                GlobalNotification.error(`${i18n.t('responsible_person_info_component_message_fail_untie_group_leader')}`);
            }
            // 关闭删除弹窗
            this.updateDeleteDialogConfig({
                visible: false,
                title: '',
                content: '',
            });
        } catch (error) {
            GlobalNotification.error(`${i18n.t('responsible_person_info_component_message_fail_untie_group_leader')}`);
        }
    }

}

type GroupProps = {
    // 集团_id
    belongedGroupId?: string;
    // 集团 string_id
    belongedGroup?: string;
    // 组件类型
    componentType?: 'EDIT' | 'PREVIEW' | 'ADD';
}

type GroupInfoData = {
    // 集团编辑信息
    groupEditDetail: Group;
}
